import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, ReferenceArea } from 'recharts';
import { formatDate } from '../../utils/formatters';

const CustomTooltip = ({ active, payload, label, anomalies }) => {
  if (!active || !payload) return null;
  const isAnomaly = anomalies?.includes(label);
  return (
    <div className={`bg-surface-700 border rounded-lg p-3 shadow-xl ${isAnomaly ? 'border-red-500' : 'border-surface-500'}`}>
      <p className="text-gray-400 text-xs mb-1">{label}</p>
      {isAnomaly && <p className="text-red-400 text-xs font-semibold mb-1">Anomaly Detected</p>}
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function AnomalyTrendChart({ data, dataKey = 'value', label = 'Value', color = '#6366f1', thresholds, anomalies = [], unit = '', higherIsBetter = true, height = 320 }) {
  if (!data || data.length === 0) {
    return <div className="card flex items-center justify-center h-64 text-gray-500">No trend data available</div>;
  }

  // Format data dates
  const chartData = data.map(d => ({
    ...d,
    date: formatDate(d.period || d.date),
    value: parseFloat(d.value) || 0,
  }));

  const formattedAnomalies = anomalies.map(a => formatDate(a));

  return (
    <div className="card">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2e3354" />
          <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} />
          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip anomalies={formattedAnomalies} />} />

          {/* Warning threshold line */}
          {thresholds?.warning != null && (
            <ReferenceLine
              y={thresholds.warning}
              stroke="#f59e0b"
              strokeDasharray="6 4"
              label={{ value: `Warning: ${thresholds.warning}${unit}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }}
            />
          )}

          {/* Critical threshold line */}
          {thresholds?.critical != null && (
            <ReferenceLine
              y={thresholds.critical}
              stroke="#ef4444"
              strokeDasharray="6 4"
              label={{ value: `Critical: ${thresholds.critical}${unit}`, position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }}
            />
          )}

          {/* Anomaly dots highlighted */}
          <Line
            type="monotone"
            dataKey="value"
            name={label}
            stroke={color}
            strokeWidth={2}
            dot={(props) => {
              const isAnomaly = formattedAnomalies.includes(props.payload.date);
              if (isAnomaly) {
                return (
                  <circle
                    key={props.key}
                    cx={props.cx}
                    cy={props.cy}
                    r={6}
                    fill="#ef4444"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                );
              }
              return null;
            }}
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
